<template>
    <section class="flex flex-col gap-8 text-base">
        <CategoryHeader />
        <p class="container-left">{{ categoryDescription }}</p>
        <template v-if="hasChildren">
            <CategorySubcategories />
            <CategoryLeadTime v-if="!isManufacturerPage" />
        </template>
        <template v-else>
            <CategoryManufacturers v-if="!isManufacturerPage" />
            <CategoryLeadTime v-if="!isManufacturerPage" />
        </template>
        <template v-if="showParametricSearch">
            <h2 class="container-left my-0">{{ $t("Global.Parts") }}</h2>
            <CategoryFilters />
            <CategoryParts />
        </template>
    </section>
</template>
<script setup lang="ts">
const globalConfig = useStateGlobalConfig();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const filterValues = ref<FilterValues>({ inStock: false, manufacturers: [], specs: [] });

const pathString = computed(() => (Array.isArray(route.params.path) ? route.params.path.join("/") : route.params.path));

const { data: category } = await useApi<Category>(`categories/${pathString.value}`);
const { data: filterOptions } = await useApi<FilterOption[]>(`category/${category.value?.Id}/filters`);
const { data: manufacturers } = category.value?.Manufacturer
    ? { data: ref<Manufacturer[]>([]) }
    : await useApi<Manufacturer[]>(`category/${category.value?.Id}/manufacturers`, {
          onResponse({ response }) {
              // category manufacturers are always sorted by parts in stock descending
              const mfrList = response._data as Manufacturer[] | null;
              if (Array.isArray(mfrList)) mfrList.sort((a, b) => b.PartsInStock - a.PartsInStock);
          },
      });

if (category.value?.Manufacturer) {
    const mfrPath = category.value.Manufacturer.VirtualPath;
    const pathParts = [...route.params.path]; // copy
    const lastPathPart = pathParts.pop();
    if (mfrPath != lastPathPart) {
        pathParts.push(mfrPath);
        await router.replace({ params: { path: pathParts } });
    }
}

if (filterOptions.value) filterValues.value = getCategoryFilterValues(filterOptions.value, route.query);

const title = computed(() => {
    if (!category.value) return "";
    if (category.value.Manufacturer) return [category.value.Name, category.value.Manufacturer.Name].join(" - ");
    return category.value.Name;
});

const description = computed(() => {
    if (!category.value) return "";
    if (category.value.Manufacturer)
        return t("PartCategory.MetaDescriptionManufacturer", [category.value.Name, category.value.Manufacturer.Name]);
    return t("PartCategory.MetaDescription", [category.value.Name]);
});

useSeoMeta({ title, ogTitle: title, description, ogDescription: description });

const children = computed<Category[]>(() => category.value?.Children ?? []);

const hasChildren = computed(() => children.value.length > 0);

const isManufacturerPage = computed(() => {
    return !!category.value?.Manufacturer;
});

const categoryDescription = computed(() => {
    if (!category.value) return "";
    if (category.value.Description) return category.value.Description;

    if (hasChildren.value) {
        const [firstCategory] = children.value;
        return t("PartCategory.DefaultCategoryDescription", [category.value.Name, firstCategory?.Name]);
    }

    if (category.value.Manufacturer) {
        return t("PartCategory.DefaultCategoryManufacturerDescription", [
            category.value.Name,
            category.value.Manufacturer.Name,
        ]);
    }

    const [firstManufacturer] = manufacturers.value ?? [];
    return t("PartCategory.DefaultCategoryLeafDescription", [category.value.Name, firstManufacturer?.Name]);
});

// show filters and parts if this is a leaf node category or if parametric search is enabled on this category
const showParametricSearch = computed(() => {
    if (!hasChildren.value) return true;
    return globalConfig.value.Features.ALL_CATEGORIES_PARAMETRIC_SEARCH
        ? category.value?.IsParametricSearchEnabled
        : false;
});

provide(CategoryInjectKey, { category, manufacturers, filterOptions, filterValues });
</script>
